import React from "react";
import { FileText, AlertTriangle } from "lucide-react";
import { fmtDate } from "../../utils";
import type { DocRegisterEntry, DocRegisterVersion } from "../../utils";
import { DownloadLink } from "../common/DownloadLink";

export interface DocumentsPanelProps {
  /** wsId → document register entries accumulated over the run. */
  register: Record<string, DocRegisterEntry[]>;
}

const subHd: React.CSSProperties = { fontSize: 10, opacity: 0.7, margin: "6px 0 3px" };

/* Pick the index of the entry's current ("Latest") version. The doc_register
   agent's authoritative verdict (is_current) wins when present; otherwise we
   fall back to the entry's current_version (seed replays with no verdict). */
function latestIndex(d: DocRegisterEntry): number {
  const versions = d.versions || [];
  const byVerdict = versions.reduce(
    (acc, v, i) => (v.is_current === true ? i : acc),
    -1,
  );
  if (byVerdict !== -1) return byVerdict;
  let idx = -1;
  versions.forEach((v, i) => {
    if (v.version === d.current_version) idx = i;
  });
  return idx;
}

/* The "Documents" tab — the run's document register grouped into the current
   (Latest) copy of each entry and everything it superseded (Old). Grouping is
   driven by the doc_register agent's authoritative verdict, not version number,
   so a lawyer-asserted operative draft lands in Latest even if a higher-numbered
   draft exists. authority_conflict entries surface a lawyer-in-the-loop banner. */
export function DocumentsPanel({ register }: DocumentsPanelProps): JSX.Element {
  const latest: DocRegisterVersion[] = [];
  const old: DocRegisterVersion[] = [];
  const conflicts: Array<{ name: string; reason?: string }> = [];
  Object.values(register)
    .flat()
    .forEach((d) => {
      const versions = d.versions || [];
      const latestIdx = latestIndex(d);
      versions.forEach((v, i) => {
        (i === latestIdx ? latest : old).push(v);
      });
      if (d.authority_conflict)
        conflicts.push({ name: d.name, reason: d.authority_conflict_reason });
    });
  if (!latest.length && !old.length)
    return (
      <div className="empty">
        No documents registered yet.
        <br />
        Attachments surface here as the spine runs.
      </div>
    );
  const row = (v: DocRegisterVersion, i: number, dim: boolean): JSX.Element => (
    <div key={i} className="docsubrow" style={dim ? { opacity: 0.6 } : undefined}>
      <FileText size={11} color={dim ? "var(--tert)" : "var(--muted)"} />
      <span className={"mono" + (dim ? " tert" : "")} style={{ fontSize: 11, color: dim ? undefined : "var(--fg)" }}>
        {v.filename}
      </span>
      <span className="mono tert" style={{ fontSize: 10 }}>
        v{v.version}
        {v.date ? " · " + fmtDate(v.date) : ""}
        {v.authority_source === "lawyer_asserted" ? " · lawyer-asserted" : ""}
      </span>
      {v.password && (
        <span className="mono" style={{ fontSize: 10, color: "var(--fg)" }}>
          Password: {v.password}
        </span>
      )}
      <DownloadLink doc={v} size={11} variant="sm" />
    </div>
  );
  return (
    <div>
      <div className="up" style={{ marginBottom: 4 }}>Documents</div>
      {conflicts.map((c, i) => (
        <div key={"conf" + i} className="docconflict" style={{
          display: "flex", gap: 6, alignItems: "flex-start", fontSize: 10,
          color: "var(--red)", margin: "4px 0", lineHeight: 1.3,
        }}>
          <AlertTriangle size={12} style={{ flexShrink: 0, marginTop: 1 }} />
          <span>
            <strong>Version authority conflict — awaiting lawyer:</strong> {c.name}
            {c.reason ? " — " + c.reason : ""}
          </span>
        </div>
      ))}
      {latest.length > 0 && (
        <>
          <div className="up" style={subHd}>Latest</div>
          {latest.map((v, i) => row(v, i, false))}
        </>
      )}
      {old.length > 0 && (
        <>
          <div className="up" style={{ ...subHd, marginTop: 8 }}>Old</div>
          {old.map((v, i) => row(v, i, true))}
        </>
      )}
    </div>
  );
}
